import React from 'react';
import { useTranslation } from 'react-i18next';
import RunnerAvatar from './RunnerAvatar';
import type { RaceFinishRow } from './RaceFinishModal';
import {
    IcCrown, IcMedal, IcClock, IcCheckCircle, IcRun, IcTurtle,
} from 'components/common/icons/EmojiIcons';
import { Button, Section, SectionHeader } from 'components/ds';
import styles from './RaceResultCard.module.css';

export interface RaceResultCardProps {
    rows: RaceFinishRow[];
    lateFeeAmount: number;
    shuttlePenaltyEnabled: boolean;
    currentUserNo: number | null;
    isHost?: boolean;
    onOpenDetail?: () => void;
    onApproveExcuse?: (targetUserNo: number, approved: boolean) => void;
}

const RaceResultCard: React.FC<RaceResultCardProps> = ({
    rows,
    lateFeeAmount,
    shuttlePenaltyEnabled,
    currentUserNo,
    isHost = false,
    onOpenDetail,
    onApproveExcuse,
}) => {
    const { t } = useTranslation();
    if (!rows || rows.length === 0) return null;

    const topThree = rows.slice(0, 3);
    const myRow = currentUserNo != null ? rows.find(r => r.userNo === currentUserNo) : null;
    const shuttleRow = rows.find(r => r.isLast);
    const pendingExcuses = rows.filter(r => r.excuseReason && !r.excuseApproved);
    const lateCount = rows.filter(r => !r.arrived && r.lateMinutes >= 10).length;

    return (
        <Section divided key="race-result">
            <SectionHeader
                title={t('race.raceResult')}
                moreLabel={onOpenDetail ? t('race.viewAll') : undefined}
                onMore={onOpenDetail}
            />

            {/* 내 결과 한 줄 */}
            {myRow && (
                <p className={styles.mine}>
                    {myRow.rank === 1 && <><IcCrown size={15} /> {t('race.youWin')} {t('race.perk1')}</>}
                    {(myRow.rank === 2 || myRow.rank === 3) && (
                        <><IcMedal size={15} /> {t('race.rankArrivedExcl', { rank: myRow.rank })}</>
                    )}
                    {myRow.rank > 3 && !myRow.isLast && !myRow.arrived && myRow.lateMinutes >= 10 && (
                        <>
                            <IcClock size={15} /> {t('race.lateMin', { n: myRow.lateMinutes })}
                            {!myRow.excuseApproved && lateFeeAmount > 0 && (
                                <span className={styles.mineFee}>
                                    · {t('race.feePending', { amount: lateFeeAmount.toLocaleString() })}
                                </span>
                            )}
                            {myRow.excuseApproved && (
                                <span className={styles.mineNote}>
                                    · <IcCheckCircle size={13} /> {t('race.excuseApprovedFull')}
                                </span>
                            )}
                        </>
                    )}
                    {myRow.isLast && <><IcTurtle size={15} /> {t('race.shuttleWin')}</>}
                    {myRow.rank > 3 && myRow.arrived && !myRow.isLast && (
                        <><IcRun size={15} /> {t('race.rankArrived', { rank: myRow.rank })}</>
                    )}
                </p>
            )}

            {/* 상위 세 명 */}
            <ol className={styles.podium}>
                {topThree.map(r => (
                    <li key={r.userNo} className={styles.pod}>
                        <RunnerAvatar
                            state={r.arrived ? 'arrived' : 'rush'}
                            rank={r.rank <= 3 ? r.rank : undefined}
                            size={40}
                            nickname={r.nickname}
                            profileImgUrl={r.profileImgUrl}
                        />
                        <span className={styles.podName}>{r.nickname}</span>
                        <span className={styles.podRank}>{t('race.rankN', { rank: r.rank })}</span>
                    </li>
                ))}
            </ol>

            {/* 요약 */}
            {((shuttlePenaltyEnabled && shuttleRow) || (lateCount > 0 && lateFeeAmount > 0)) && (
                <ul className={styles.facts}>
                    {shuttlePenaltyEnabled && shuttleRow && (
                        <li className={styles.fact}>
                            <IcTurtle size={14} /> {t('race.todayShuttle')} <strong>{shuttleRow.nickname}</strong>
                        </li>
                    )}
                    {lateCount > 0 && lateFeeAmount > 0 && (
                        <li className={styles.fact}>
                            <IcClock size={14} /> {t('race.lateSummary', { count: lateCount, fee: lateFeeAmount.toLocaleString() })}
                        </li>
                    )}
                </ul>
            )}

            {/* 주최자 — 승인 대기 중인 사유 */}
            {isHost && pendingExcuses.length > 0 && (
                <>
                    <p className={styles.excuseTitle}>
                        {t('race.pendingApproval', { count: pendingExcuses.length })}
                    </p>
                    <ul className={styles.list}>
                        {pendingExcuses.map(r => (
                            <li key={r.userNo} className={styles.row}>
                                <span className={styles.rowBody}>
                                    <span className={styles.rowName}>{r.nickname}</span>
                                    <span className={styles.rowMeta}>{r.excuseReason}</span>
                                </span>
                                {onApproveExcuse && (
                                    <span className={styles.rowActions}>
                                        <Button size="sm" variant="secondary" onClick={() => onApproveExcuse(r.userNo, true)}>
                                            {t('race.approve')}
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => onApproveExcuse(r.userNo, false)}>
                                            {t('race.reject')}
                                        </Button>
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </Section>
    );
};

export default RaceResultCard;
